/**
 * UserRegister Component
 * Registration form with security features
 * Redesigned: mobile-first clean UI with icons
 * When designColors prop is provided, uses inline styles (custom designs).
 * When designColors is absent, uses isDarkMode Tailwind classes (classic design).
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';

const UserRegister = ({ API, onSuccess, onSwitchToLogin, isDarkMode, designColors, loginHidden }) => {
  const { t } = useTranslation();
  const dc = designColors; // shorthand, null for classic
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    username: '',
    captchaAnswer: '',
    termsAccepted: false,
    customFields: {}
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [securitySettings, setSecuritySettings] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadSecuritySettings();
  }, []);

  const loadSecuritySettings = async () => {
    try {
      const response = await fetch(`${API}/security/settings/public`);
      const data = await response.json();
      setSecuritySettings(data);
      if (data.captcha_enabled) {
        loadCaptcha();
      }
    } catch (error) {
      console.error('Error loading security settings:', error);
    }
  };

  const loadCaptcha = async () => {
    try {
      const response = await fetch(`${API}/security/captcha`);
      const data = await response.json();
      if (data.captcha_required) {
        setCaptcha(data);
      }
    } catch (error) {
      console.error('Error loading CAPTCHA:', error);
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    try {
      const response = await fetch(`${API}/auth/check-username?username=${encodeURIComponent(username)}`);
      const data = await response.json();
      setUsernameAvailable(data.available);
    } catch (error) {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (captcha && captcha.captcha_required) {
        const captchaVerify = await fetch(`${API}/security/verify-captcha`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question_id: captcha.question_id, answer: formData.captchaAnswer })
        });
        const captchaResult = await captchaVerify.json();
        if (!captchaResult.valid) {
          setError(t('error_captcha'));
          loadCaptcha();
          setFormData({...formData, captchaAnswer: ''});
          setLoading(false);
          return;
        }
      }

      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('full_name', formData.full_name);

      if (securitySettings?.field_visibility?.phone && formData.phone) {
        formDataToSend.append('phone', formData.phone);
      }
      if (securitySettings?.field_visibility?.username && formData.username) {
        formDataToSend.append('username', formData.username);
      }
      if (captcha) {
        formDataToSend.append('captcha_question_id', captcha.question_id);
        formDataToSend.append('captcha_answer', formData.captchaAnswer);
      }
      if (securitySettings?.terms_enabled) {
        formDataToSend.append('terms_accepted', formData.termsAccepted);
      }
      if (Object.keys(formData.customFields).length > 0) {
        formDataToSend.append('custom_fields', JSON.stringify(formData.customFields));
      }

      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requires_verification) {
          setVerificationSent(true);
          setVerificationEmail(data.email);
        } else {
          localStorage.setItem('user_token', data.access_token);
          localStorage.setItem('user_data', JSON.stringify(data.user));
          onSuccess(data.user, data.access_token);
        }
      } else {
        setError(data.detail || 'Registration failed');
        if (captcha) loadCaptcha();
      }
    } catch (error) {
      setError(t('error_network'));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (!securitySettings) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  // Email verification success
  if (verificationSent) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('check_email')}
        </h2>
        <p className={`mb-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {t('verification_sent')}
        </p>
        <p className="font-semibold text-blue-600 mb-4">{verificationEmail}</p>
        <p className={`text-xs mb-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('check_spam')}
        </p>
        {!loginHidden && (
          <button
            onClick={onSwitchToLogin}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-sm"
          >
            {t('go_to_login')}
          </button>
        )}
      </div>
    );
  }

  // Style helpers: dc = custom design colors, otherwise classic Tailwind
  const labelStyle = dc ? { color: dc.labelColor } : undefined;
  const labelClass = dc
    ? 'block text-sm font-medium mb-1.5'
    : `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const iconStyle = dc ? { color: dc.iconColor } : undefined;
  const iconClass = dc
    ? 'absolute left-3 top-1/2 -translate-y-1/2'
    : `absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;

  const inputStyle = dc
    ? { backgroundColor: dc.inputBg, borderColor: dc.inputBorder, color: dc.inputText }
    : undefined;
  const inputClass = dc
    ? 'w-full pl-10 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : `w-full pl-10 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`;

  const secondaryStyle = dc ? { color: dc.secondaryText } : undefined;
  const linkStyle = dc ? { color: dc.linkColor } : undefined;
  const buttonStyle = dc ? { background: dc.buttonBg, color: dc.buttonText } : undefined;

  const placeholderId = dc ? 'register-dc-inputs' : '';

  return (
    <div>
      {dc && (
        <style>{`
          .${placeholderId} input::placeholder { color: ${dc.inputPlaceholder} !important; }
        `}</style>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={`space-y-4 ${placeholderId}`} autoComplete="off">
        <input type="text" name="website" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

        {/* Full Name */}
        {securitySettings.field_visibility?.full_name && (
          <div>
            <label className={labelClass} style={labelStyle}>
              {t('full_name')} {securitySettings.field_required?.full_name && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className={iconClass} style={iconStyle}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                required={securitySettings.field_required?.full_name}
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className={inputClass}
                style={inputStyle}
                placeholder="Your full name"
              />
            </div>
          </div>
        )}

        {/* Email */}
        <div>
          <label className={labelClass} style={labelStyle}>
            {t('email')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className={iconClass} style={iconStyle}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className={inputClass}
              style={inputStyle}
              placeholder="you@example.com"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className={labelClass} style={labelStyle}>
            {t('password')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className={iconClass} style={iconStyle}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              minLength="10"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className={dc
                ? 'w-full pl-10 pr-12 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                : `w-full pl-10 pr-12 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`}
              style={inputStyle}
              placeholder="Min 10 characters"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={dc ? 'absolute right-3 top-1/2 -translate-y-1/2' : `absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              style={iconStyle}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          <p
            className={dc ? 'text-xs mt-1.5' : `text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}
            style={secondaryStyle}
          >
            {t('password_hint')}
          </p>
        </div>

        {/* Phone */}
        {securitySettings.field_visibility?.phone && (
          <div>
            <label className={labelClass} style={labelStyle}>
              {t('phone')} {securitySettings.field_required?.phone && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className={iconClass} style={iconStyle}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <input
                type="tel"
                required={securitySettings.field_required?.phone}
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className={inputClass}
                style={inputStyle}
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>
        )}

        {/* Username */}
        {securitySettings.field_visibility?.username && (
          <div>
            <label className={labelClass} style={labelStyle}>
              {t('username')} {securitySettings.field_required?.username && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              <div className={iconClass} style={iconStyle}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                type="text"
                required={securitySettings.field_required?.username}
                value={formData.username}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({...formData, username: value});
                  if (value.length >= 3) {
                    setTimeout(() => checkUsernameAvailability(value), 500);
                  } else {
                    setUsernameAvailable(null);
                  }
                }}
                className={dc
                  ? `w-full pl-10 pr-10 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${usernameAvailable === false ? 'border-red-400 focus:ring-red-500' : usernameAvailable === true ? 'border-green-400 focus:ring-green-500' : ''}`
                  : `w-full pl-10 pr-10 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'} ${usernameAvailable === false ? 'border-red-400 focus:ring-red-500' : usernameAvailable === true ? 'border-green-400 focus:ring-green-500' : ''}`}
                style={inputStyle}
                placeholder="johndoe"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingUsername && <svg className="animate-spin w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
                {!checkingUsername && usernameAvailable === true && <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                {!checkingUsername && usernameAvailable === false && <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>}
              </div>
            </div>
            {usernameAvailable === false && <p className="text-red-500 text-xs mt-1">{t('username_taken')}</p>}
            {usernameAvailable === true && <p className="text-green-500 text-xs mt-1">{t('username_available')}</p>}
          </div>
        )}

        {/* CAPTCHA */}
        {captcha && captcha.captcha_required && (
          <div
            className={dc ? 'p-4 rounded-xl border' : `p-4 rounded-xl border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50/50 border-blue-100'}`}
            style={dc ? { backgroundColor: dc.inputBg, borderColor: dc.inputBorder } : undefined}
          >
            <label className={dc ? 'block text-sm font-medium mb-1.5' : `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} style={labelStyle}>
              {t('security_question')} <span className="text-red-500">*</span>
            </label>
            <p className={dc ? 'text-sm mb-2' : `text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={secondaryStyle}>{captcha.question}</p>
            <input
              type="text"
              required
              value={formData.captchaAnswer}
              onChange={(e) => setFormData({...formData, captchaAnswer: e.target.value})}
              className={dc
                ? 'w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                : `w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-600/50 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'}`}
              style={inputStyle}
              placeholder={t('your_answer')}
            />
          </div>
        )}

        {/* Terms */}
        {securitySettings.terms_enabled && (
          <div className="flex items-start gap-2.5">
            <input
              type="checkbox"
              id="terms"
              required
              checked={formData.termsAccepted}
              onChange={(e) => setFormData({...formData, termsAccepted: e.target.checked})}
              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded"
              style={dc ? { accentColor: dc.linkColor } : undefined}
            />
            <label
              htmlFor="terms"
              className={dc ? 'text-sm' : `text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
              style={secondaryStyle}
            >
              {t('terms_accept')} <a href={securitySettings.terms_link} target="_blank" rel="noopener noreferrer" className={dc ? 'hover:underline font-medium' : 'text-blue-600 hover:underline font-medium'} style={linkStyle}>{t('terms_link')}</a>
              <span className="text-red-500"> *</span>
            </label>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className={dc
            ? 'w-full py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 shadow-lg'
            : 'w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold text-sm hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/25'}
          style={buttonStyle}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('creating_account')}
            </span>
          ) : t('sign_up')}
        </button>
      </form>

      {/* Switch to login */}
      {!loginHidden && (
        <p
          className={dc ? 'mt-6 text-center text-sm' : `mt-6 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          style={secondaryStyle}
        >
          {t('have_account')}{' '}
          <button
            onClick={onSwitchToLogin}
            className={dc ? 'font-semibold' : 'text-blue-600 hover:text-blue-700 font-semibold'}
            style={linkStyle}
          >{t('sign_in')}</button>
        </p>
      )}
    </div>
  );
};

export default UserRegister;
